import React, { useState } from 'react';
import UserInfoPage from './pages/UserInfoPage';
import UploadDocumentsPage from './pages/UploadDocumentsPage';
import AnalysisQuestionsPage from './pages/AnalysisQuestionsPage';
import ReportDownloadPage from './pages/ReportDownloadPage';

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [userInfo, setUserInfo] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    interests: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analysisSelections, setAnalysisSelections] = useState({
    round1: '',
    round2: '',
    round3: ''
  });
  const [reportInfo, setReportInfo] = useState(null);

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <UserInfoPage
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <UploadDocumentsPage
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <AnalysisQuestionsPage
            selections={analysisSelections}
            setSelections={setAnalysisSelections}
            onNext={nextStep}
            onBack={prevStep}
            userInfo={userInfo}
            uploadedFiles={uploadedFiles}
            setReportInfo={setReportInfo}
          />
        );
      case 4:
        return (
          <ReportDownloadPage
            reportInfo={reportInfo}
            uploadedFiles={uploadedFiles}
            selections={analysisSelections}
            onStartOver={() => {
              setCurrentStep(1);
              setUserInfo({
                full_name: '',
                email: '',
                phone: '',
                address: '',
                interests: ''
              });
              setUploadedFiles([]);
              setAnalysisSelections({
                round1: '',
                round2: '',
                round3: ''
              });
              setReportInfo(null);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border py-6">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-primary mb-2">
              ◆ CorpAnalyzer
            </h1>
            <p className="text-text-secondary">
              AI-Powered Corporate SEC Filing Analysis
            </p>
          </div>
        </div>
      </header>

      {/* Progress Indicator */}
      {currentStep < 4 && (
        <div className="bg-white border-b border-border py-4">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center justify-center space-x-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      step === currentStep
                        ? 'bg-primary text-white'
                        : step < currentStep
                        ? 'bg-success text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`w-16 h-1 ${
                        step < currentStep ? 'bg-success' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-2">
              <p className="text-sm text-text-secondary">
                Step {currentStep} of 4
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {renderStep()}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-border mt-12 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-text-secondary mb-2">
            Powered by Claude AI
          </p>
          <p className="text-xs text-text-secondary">
            © 2026 CorpAnalyzer.com • 1 Floppy Drive, Washington DC 20001 •{' '}
            <a href="mailto:support@corpanalyzer.com" className="text-primary hover:underline">
              support@corpanalyzer.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
